type ViteEnv = {
  MODE?: string;
  DEV?: boolean;
  NODE_ENV?: string;
  VITE_API_BASE_URL?: string;
  VITE_CENTIFIC_SSO_URL?: string;
  VITE_CENTIFIC_CLIENT_ID?: string;
  VITE_SSO_REDIRECT_URL?: string;
  VITE_PRODUCTION_REDIRECT_URL?: string;
  VITE_ENTRA_AUTH_ENABLED?: string;
  VITE_AZURE_AD_CLIENT_ID?: string;
  VITE_AZURE_AD_TENANT_ID?: string;
  VITE_AZURE_AD_REDIRECT_URI?: string;
  VITE_AZURE_CONTAINER?: string;
  VITE_ENABLE_PARTICIPANT_INVITE?: string;
  VITE_ENABLE_PAYMENT_APPROVALS?: string;
  VITE_ENABLE_JOURNAL_ENTRY?: string;
  VITE_BOOKING_OPTIONAL_MODERATOR?: string;
  VITE_ENABLE_PROJECT_EMAIL_TEMPLATES?: string;
  VITE_ENABLE_BULK_EMAIL_TEMPLATE_IMPORT?: string;
  VITE_ENABLE_SMS_SETUP?: string;
  VITE_ENABLE_PROJECT_MODULES_TAB?: string;
};

const processEnv =
  typeof process !== "undefined" && process.env
    ? (process.env as Partial<ViteEnv>)
    : {};

const viteEnvStatic: ViteEnv = {
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_CENTIFIC_SSO_URL: import.meta.env.VITE_CENTIFIC_SSO_URL,
  VITE_CENTIFIC_CLIENT_ID: import.meta.env.VITE_CENTIFIC_CLIENT_ID,
  VITE_SSO_REDIRECT_URL: import.meta.env.VITE_SSO_REDIRECT_URL,
  VITE_PRODUCTION_REDIRECT_URL: import.meta.env.VITE_PRODUCTION_REDIRECT_URL,
  VITE_ENTRA_AUTH_ENABLED: import.meta.env.VITE_ENTRA_AUTH_ENABLED,
  VITE_AZURE_AD_CLIENT_ID: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
  VITE_AZURE_AD_TENANT_ID: import.meta.env.VITE_AZURE_AD_TENANT_ID,
  VITE_AZURE_AD_REDIRECT_URI: import.meta.env.VITE_AZURE_AD_REDIRECT_URI,
  VITE_AZURE_CONTAINER: import.meta.env.VITE_AZURE_CONTAINER,
  VITE_ENABLE_PARTICIPANT_INVITE: import.meta.env
    .VITE_ENABLE_PARTICIPANT_INVITE,
  VITE_ENABLE_PAYMENT_APPROVALS: import.meta.env.VITE_ENABLE_PAYMENT_APPROVALS,
  VITE_ENABLE_JOURNAL_ENTRY: import.meta.env.VITE_ENABLE_JOURNAL_ENTRY,
  VITE_BOOKING_OPTIONAL_MODERATOR: import.meta.env
    .VITE_BOOKING_OPTIONAL_MODERATOR,
  VITE_ENABLE_PROJECT_EMAIL_TEMPLATES: import.meta.env
    .VITE_ENABLE_PROJECT_EMAIL_TEMPLATES,
  VITE_ENABLE_BULK_EMAIL_TEMPLATE_IMPORT: import.meta.env
    .VITE_ENABLE_BULK_EMAIL_TEMPLATE_IMPORT,
  VITE_ENABLE_SMS_SETUP: import.meta.env.VITE_ENABLE_SMS_SETUP,
  VITE_ENABLE_PROJECT_MODULES_TAB: import.meta.env
    .VITE_ENABLE_PROJECT_MODULES_TAB,
};

export const env: ViteEnv = {
  ...processEnv,
  ...viteEnvStatic,
  MODE: viteEnvStatic.MODE || processEnv.NODE_ENV,
  DEV:
    typeof viteEnvStatic.DEV === "boolean"
      ? viteEnvStatic.DEV
      : processEnv.NODE_ENV !== "production",
};

/** Defaults to on; set `VITE_ENABLE_PAYMENT_APPROVALS=false` to hide payment approval features. Uses static `import.meta.env` so Vite inlines `.env` values. */
export const isPaymentApprovalsEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_PAYMENT_APPROVALS !== "false";

/** Defaults to off; set `VITE_ENABLE_JOURNAL_ENTRY=true` to show journal-entry features in Upload/Submit flows. */
export const isJournalEntryEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_JOURNAL_ENTRY === "true";

/** Default ON unless explicitly disabled; set `VITE_BOOKING_OPTIONAL_MODERATOR=false` for legacy moderator-required booking. */
export const isBookingOptionalModeratorEnabled = (): boolean =>
  import.meta.env.VITE_BOOKING_OPTIONAL_MODERATOR !== "false";

/** Defaults to off; set `VITE_ENABLE_PROJECT_EMAIL_TEMPLATES=true` to show the project Email Setup tab. */
export const isProjectEmailTemplatesEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_PROJECT_EMAIL_TEMPLATES === "true";

/** Defaults to off; set `VITE_ENABLE_BULK_EMAIL_TEMPLATE_IMPORT=true` to show the bulk download/upload template controls. */
export const isBulkEmailTemplateImportEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_BULK_EMAIL_TEMPLATE_IMPORT === "true";

/** Defaults to on; set `VITE_ENABLE_PROJECT_CONFIGURATION=false` to hide the PM Project Configuration tab (OD-1089 bonus / partial-payment defaults). */
export const isProjectConfigurationEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_PROJECT_CONFIGURATION !== "false";

/** Defaults to on; set `VITE_ENABLE_BULK_CANCEL=false` to hide the Scheduler Bulk Cancel action. */
export const isBulkCancelEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_BULK_CANCEL !== "false";

/**
 * Defaults to OFF; set `VITE_ENABLE_BULK_CANCEL_PAYMENT_OPTIONS=true` to show the
 * Bulk Cancel payment panel (With/Without Payment, the partial-payment categories,
 * and the "Allow future bookings" checkbox) as built under OD-1101.
 *
 * Off until OneForma can handle partial payments: bulk cancel is then reason-only
 * — select sessions, enter a reason, cancel immediately, with no partial-payment
 * approval request and no future-booking flag applied. One flag covers the whole
 * panel; the payment behaviour stays in place behind it for later enablement.
 */
export const isBulkCancelPaymentOptionsEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_BULK_CANCEL_PAYMENT_OPTIONS === "true";

/** Defaults to off; set `VITE_ENTRA_AUTH_ENABLED=true` to show the direct Microsoft (Entra ID) sign-in option. */
export const isEntraAuthEnabled = (): boolean =>
  import.meta.env.VITE_ENTRA_AUTH_ENABLED === "true";

/** Defaults to off; set `VITE_ENABLE_SMS_SETUP=true` to show the Project Setup SMS Setup tab (layout preview only, not yet persisted). */
export const isSmsSetupEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_SMS_SETUP === "true";

/** Defaults to off; set `VITE_ENABLE_PROJECT_MODULES_TAB=true` to show the Project Setup Project Modules tab (layout placeholder, no persistence yet). */
export const isProjectModulesTabEnabled = (): boolean =>
  import.meta.env.VITE_ENABLE_PROJECT_MODULES_TAB === "true";
